import { supabase } from '@/integrations/supabase/client';
import type { TranslationKey } from '@/types/i18n.types';

/**
 * Checkout Stripe dell'itinerario per l'utente autenticato.
 *
 * Passa dalla edge function create-itinerary-payment-link, che rilegge dal database
 * l'importo — soprattutto quello di una singola rata: il frontend manda solo degli id.
 *
 * L'URL che torna e' una Checkout Session monouso, valida un'ora. Non e' un link da
 * conservare o inoltrare: va aperto subito, ed e' proprio questo che rende sicuro
 * ritoccare una rata non ancora pagata.
 */

export interface CreateItineraryPaymentLinkParams {
    itineraryId: string;
    /** Se valorizzato si paga UNA rata invece dell'intero itinerario. */
    installmentId?: string;
    customerEmail?: string | null;
}

export const createItineraryPaymentLink = async ({
    itineraryId,
    installmentId,
    customerEmail,
}: CreateItineraryPaymentLinkParams): Promise<string> => {
    const { data, error } = await supabase.functions.invoke('create-itinerary-payment-link', {
        body: {
            itineraryId,
            ...(installmentId ? { installmentId } : {}),
            ...(customerEmail ? { customerEmail } : {}),
        },
    });

    if (error) throw error;

    const result = data as { success?: boolean; url?: string; error?: string } | null;
    if (!result?.success || !result.url) {
        throw new Error(result?.error ?? 'payment_link_failed');
    }

    return result.url;
};

/**
 * Traduce gli errori della edge function in chiavi i18n.
 * I codici tornati dal server sono identificatori stabili, non testo da mostrare.
 */
export const itineraryPaymentErrorKey = (error: unknown): TranslationKey => {
    const message = (error as { message?: string })?.message ?? '';

    const known = [
        'forbidden',
        'itinerary_not_found',
        'no_assigned_designer',
        'advisor_not_found',
        'stripe_not_configured',
        'installment_not_found',
        'installment_not_in_itinerary',
        'installment_not_payable',
        // Guard sul doppio pagamento. Non sono errori dell'utente ma la difesa contro un
        // secondo addebito vero: si arriva qui con una scheda rimasta aperta, o tornando a
        // pagare mentre un bonifico e' ancora in viaggio. Vanno spiegati, non liquidati
        // con "riprova" — che e' esattamente la cosa da non fare.
        'itinerary_already_paid',
        'invalid_amount',
    ];

    const match = known.find((key) => message.includes(key));
    return (match
        ? `itinerary.payment.errors.${match}`
        : 'itinerary.payment.errors.generic') as TranslationKey;
};
